import { CartStore } from '@/cart/cart.store';
import { LayoutService } from '@/layout/service/layout.service';
import { ProductStore } from '@/products/products.store';
import { GlobalStore } from '@/store/app.store';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  untracked,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { BadgeComponent } from '@edt/components/atoms/badge/badge.component';
import { SearchComponent } from '@edt/components/atoms/search/search.component';
import { Router } from '@angular/router';
import { Option } from '@edt/components/atoms/search/models';
import { UserAction, UserData, UserMenuComponent } from '@edt/auth';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'store-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    UserMenuComponent,
    MatIconModule,
    MatButtonModule,
    BadgeComponent,
    SearchComponent,
    MatProgressSpinnerModule,
  ],
})
export class HeaderComponent {
  #layoutSrv = inject(LayoutService);
  #globalStore = inject(GlobalStore);
  #cartStore = inject(CartStore);
  #productStore = inject(ProductStore);
  #router = inject(Router);
  readonly logoImg = input.required<string>();
  readonly canShowSearch = input.required<boolean>();
  siteInfo = this.#globalStore.siteInfo;
  userData = this.#globalStore.authUserData;
  userInfo = computed((): UserData | undefined => {
    if (!this.userData()) return undefined;
    return {
      fullname: this.userData()
        ? this.userData()?.first_name + ' ' + this.userData()?.last_name
        : 'Anónimo',
      username: this.userData() ? this.userData()?.email : 'Anónimo',
    };
  });
  authUserButton = computed(() => {
    const config = this.#globalStore.authUserButton();
    if (!config) {
      return null;
    }
    return {
      ...config,
      loggedInIcon: config?.loggedInIcon,
      loggedInText:
        config?.loggedInIcon || config?.loggedInText
          ? config.loggedInText?.replace(
              '{{username}}',
              this.userData()?.email ?? ''
            )
          : 'Anónimo',
      notLoggedInIcon: config?.notLoggedInIcon,
      notLoggedInText:
        config?.notLoggedInIcon || config?.notLoggedInText
          ? config.notLoggedInText
          : 'Registrarse',
    };
  });
  id = -1;
  authUserMenu: UserAction[] = [];
  cartOpened = this.#layoutSrv.rightMenuOpened;
  leftMenuOpened = this.#layoutSrv.leftMenuOpened;
  resolvingCart = this.#cartStore.isCreatingOrRetrievingCart;
  validCart = computed(() => {
    const cart = this.#cartStore.cart();
    return cart != null;
  });
  regionId = this.#globalStore.currentRegionId;
  currencyCode = this.#globalStore.currentCurrencyCode;

  quantity = computed(
    () =>
      this.#cartStore
        .cart()
        ?.items?.reduce((sum, item) => sum + item.quantity, 0) ?? 0
  );

  // Propiedades del estado global
  searchPlaceholder =
    this.#globalStore.searchComponentConfig()?.placeholder ?? 'Buscar';
  autoSearch = this.#globalStore.searchComponentConfig()?.auto_search ?? false;
  delayAutoSearchTime =
    this.#globalStore.searchComponentConfig()?.delay_auto_search_time ?? 300;
  allowSuggestions =
    this.#globalStore.searchComponentConfig()?.allow_suggestions ?? false;
  showHistory =
    this.#globalStore.searchComponentConfig()?.show_history ?? false;
  maxHistoryItems =
    this.#globalStore.searchComponentConfig()?.max_history_items ?? 10;
  compactMode =
    this.#globalStore.searchComponentConfig()?.compact_mode ?? false;
  minSearchLength =
    this.#globalStore.searchComponentConfig()?.min_search_length ?? 3;
  searchStyles = this.#globalStore.searchComponentConfig()?.styles ?? null;

  constructor() {
    effect(() => {
      const data = this.userData();
      this.#updateUserMenu(data != null);
    });

    effect(() => {
      const globalRegionId = this.regionId();
      const currencyCode = this.currencyCode()?.toUpperCase();

      if (!globalRegionId) return;
      const cartRegionId = untracked(this.#cartStore.regionId);
      if (globalRegionId !== cartRegionId) {
        this.#cartStore.setRegionId(globalRegionId);
      }
      if (currencyCode) {
        const cartCurrency = untracked(this.#cartStore.currencyCode);
        if (currencyCode !== cartCurrency) {
          this.#cartStore.setCurrencyCode(currencyCode);
        }
      }
    });
  }

  toggleCart(ev: boolean): void {
    const cartId = this.#cartStore.cartId();
    const regionId = this.regionId();

    if (ev && cartId && regionId) {
      this.#cartStore.retrieveCart({ cartId, regionId });
    } else if (
      this.#cartStore.cart() === null &&
      !this.#cartStore.isCreatingOrRetrievingCart()
    ) {
      const regionId2 = this.#globalStore.currentRegionId();
      const currencyCode2 =
        this.#globalStore.currentCurrencyCode()?.toUpperCase() ?? null;
      if (regionId2 && regionId2?.length > 0) {
        this.#cartStore.setRegionId('');
        setTimeout(() => {
          this.#cartStore.setRegionId(regionId2);
        }, 500);
        if (currencyCode2 !== this.#cartStore.currencyCode())
          this.#cartStore.setCurrencyCode(currencyCode2);
      }
    }
    this.#layoutSrv.toggleRightMenu(ev);
  }

  togglLeftMenu(ev?: boolean): void {
    this.#layoutSrv.toggleLeftMenu(ev);
  }

  onActionDispached(ev: UserAction): void {
    switch (ev.name) {
      case 'addresses':
        this.#router.navigate(['usuario/direcciones']);
        break;
      case 'order':
        this.#router.navigate(['usuario/ordenes']);
        break;
      case 'logout':
        this.logout();
        break;
      case 'profile':
        this.#router.navigate(['usuario']);
        break;
      case 'register':
        this.#router.navigate(['/seguridad/registro']);
        break;
      case 'login':
        this.#router.navigate(['/seguridad/iniciar']);
        break;
      case 'cambiar_credenciales':
        this.goToChangePassword();
        break;
    }
  }

  logout() {
    this.#globalStore.setAuthUser(null);
    this.#router.navigate(['/']);
  }

  goToSiteHome() {
    this.#router.navigate(['/']);
    this.#globalStore.selectCategories([]);
  }

  goToChangePassword() {
    if (this.id !== -1)
      this.#router.navigate(['/usuario/cambiar_credenciales/' + this.id]);
  }
  doSearch(ev?: string) {
    this.#productStore.setSearchQuery(ev ?? null);
    this.#router.navigate(['/productos']);
  }

  optionSelected(ev: Option) {
    this.#productStore.setSearchQuery(ev.value);
  }

  #updateUserMenu(isLoggin: boolean): void {
    this.authUserMenu = isLoggin
      ? [
          {
            title: 'Perfil',
            name: 'profile',
            description: 'Ver datos de usuario',
            icon: 'NONE',
            image: 'NONE',
          },
          {
            title: 'Mis destinatarios',
            name: 'addresses',
            description: 'Ver mis destinatarios',
            icon: 'NONE',
            image: 'NONE',
          },
          {
            title: 'Mis pedidos',
            name: 'order',
            description: 'Ver mis pedidos',
            icon: 'NONE',
            image: 'NONE',
          },
          {
            title: 'Salir',
            name: 'logout',
            description: 'Salir',
            icon: 'NONE',
            image: 'NONE',
          },
        ]
      : [
          {
            title: 'Registrarse',
            name: 'register',
            description: 'Registrar usuario',
            icon: 'NONE',
            image: 'NONE',
          },
          {
            title: 'Iniciar',
            name: 'login',
            description: 'Iniciar session',
            icon: 'NONE',
            image: 'NONE',
          },
        ];
  }
}
